import React, { Fragment, useState } from "react";
import { PropTypes } from "prop-types";
import { Dialog, IconButton } from "@material-ui/core";
import Delete from "@material-ui/icons/Delete";
import axios from "axios";
import { useIntl } from "react-intl";
import ModalExcluirAtividade from "./ModalExcluirAtividade";
import DialogCancelaWorkflow from "./DialogCancelaWorkflow";
import { createSnackbar, createDialogConfirm, AjaxBlackout } from "../../../_common";
import { TIPO_ACAO } from "../../workflow/constantes";
import { useSelectPermission } from "../../../hooks";
import permissions from "../../../_common/permissions";
import { ACCESS_LEVEL } from "../../../_common/constantes";
import { ActionButton } from "../../../components";
import ModalAtividadesEncadeadas from "../../atividades/Novo/_common/ModalAtividadesEncadeadas";

function ExcluirAtividade({ atividadesSelecionadas, id, callback, styleIcon, useIconWithLabel = false, useJustLabel }) {
  const LIMITE_EXCLUSAO = 100;
  const ids = atividadesSelecionadas?.map(atividade => atividade.id);
  const [open, setOpen] = useState(false);
  const intl = useIntl();
  const permissaoExcluir = useSelectPermission(permissions.MODULOATIVIDADES);
  const [atividadeEncadeadas, setAtividadeEncadeadas] = useState(null);

  function close(didRemove = true) {
    setOpen(false);
    callback(didRemove);
  }

  function criaAtividadeEncadeada(accepted, workflowPayloadDto) {
    if (accepted) {
      AjaxBlackout.Show();
      axios.post("/api/pipeline/workflow/CriarAcoes", workflowPayloadDto).then(() => {
        AjaxBlackout.Hide();
        close();
      });
    } else {
      close();
    }
  }

  const abrirModalExcluir = async () => {
    if (ids && ids.length > LIMITE_EXCLUSAO) {
      createSnackbar(
        intl.formatMessage(
          { defaultMessage: "Selecione no máximo {qtdLimiteExclusao} atividades para excluir." },
          { qtdLimiteExclusao: LIMITE_EXCLUSAO }
        )
      );
      return;
    }

    if (permissaoExcluir.nivelAcesso !== ACCESS_LEVEL.NONE) {
      if (ids) {
        const retorno = await axios.post(`api/pipeline/Atividade/BuscarAcoesEncadeadas`, ids);
        setAtividadeEncadeadas(retorno.data);
      }
      setOpen(true);
    } else {
      createSnackbar(intl.formatMessage({ defaultMessage: "Sem Permissão." }));
    }
  };

  const montarAtividadeEnvio = () =>
    atividadesSelecionadas.map(({ id: atividadeId, nuTipoId, preVendedorId, lead, etapa }) => ({
      id: atividadeId,
      tipoAtividadeId: nuTipoId,
      leadId: lead?.id ?? null,
      preVendedorId,
      etapaId: etapa?.id ?? null,
      funilId: etapa?.funil.id ?? null,
    }));

  async function excluirNaoEncadeada() {
    try {
      AjaxBlackout.Show();

      if (!ids || ids.length === 1) {
        excluir();
      } else {
        const atividadesEnvio = montarAtividadeEnvio();
        let atividadesEnvioFiltradas = atividadesEnvio;

        if (atividadeEncadeadas) {
          const idsAtividadesEncadeadas = new Set(
            atividadeEncadeadas.flatMap(item => item.atividades.map(atividade => atividade.id))
          );
          atividadesEnvioFiltradas = atividadesEnvio.filter(item => !idsAtividadesEncadeadas.has(item.id));
        }

        const response = await axios.post("/api/pipeline/Atividade/ExcluirEmMassa", atividadesEnvioFiltradas);
        close();

        if (response.status === 200)
          createSnackbar(
            intl.formatMessage({
              defaultMessage: "Atividades excluídas com sucesso",
            })
          );
      }
    } catch (ex) {
      createSnackbar(intl.formatMessage({ defaultMessage: "Erro ao excluir atividades" }));
    } finally {
      AjaxBlackout.Hide();
    }
  }

  async function excluirComEncadeada() {
    try {
      AjaxBlackout.Show();

      const atividadesEnvio = montarAtividadeEnvio();
      const response = await axios.post("/api/pipeline/Atividade/ExcluirEmMassa", atividadesEnvio);
      close();

      if (response.status === 200)
        createSnackbar(intl.formatMessage({ defaultMessage: "Atividades excluídas com sucesso" }));
    } catch (ex) {
      createSnackbar(intl.formatMessage({ defaultMessage: "Erro ao excluir atividades" }));
    } finally {
      AjaxBlackout.Hide();
    }
  }

  function excluir() {
    try {
      AjaxBlackout.Show();
      axios.post(`/api/pipeline/atividade/cancelar?id=${id || ids[0]}`).then(({ data }) => {
        AjaxBlackout.Hide();
        createSnackbar(intl.formatMessage({ defaultMessage: "Atividade excluída com sucesso" }));
        if (data) {
          let titulo;
          let botaoConfirmar;
          let icone;
          let descricaoIcone;

          switch (data.workflowAcaoDto.tipoAcao) {
            case TIPO_ACAO.ENVIO_HSM:
              titulo = intl.formatMessage({ defaultMessage: "Enviar HSM (Workflow)" });
              botaoConfirmar = intl.formatMessage({ defaultMessage: "Enviar HSM" });
              icone = "whatsapp";
              descricaoIcone = data.workflowAcaoDto.nomeTemplateMensagemHsm;
              break;
            case TIPO_ACAO.CADENCIA:
              titulo = intl.formatMessage({ defaultMessage: "Inserir na Cadência de E-mails (Workflow)" });
              botaoConfirmar = intl.formatMessage({ defaultMessage: "Inserir na Cadência" });
              icone = "send";
              descricaoIcone = data.workflowAcaoDto.nomeCampanha;
              break;
            default:
              titulo = intl.formatMessage({ defaultMessage: "Atividade Encadeada (Workflow)" });
              botaoConfirmar = intl.formatMessage({ defaultMessage: "Criar Atividade" });
              icone = data.tipoAtividadeDto.icone;
              descricaoIcone = data.tipoAtividadeDto.descricao;
              break;
          }

          createDialogConfirm({
            text: intl.formatMessage({
              defaultMessage: "Ao excluir esta atividade, a ação encadeada abaixo será cancelada:",
            }),
            title: titulo,
            acceptLabel: botaoConfirmar,
            cancelLabel: intl.formatMessage({ defaultMessage: "Cancelar Workflow" }),
            content: <DialogCancelaWorkflow icon={icone} desc={descricaoIcone} />,
            callback: accepted => criaAtividadeEncadeada(accepted, data.workflowPayloadDto),
          });
        } else close();
      });
    } catch (ex) {
      createSnackbar(intl.formatMessage({ defaultMessage: "Erro ao excluir atividade" }));
    }
  }

  const verificarSomenteEncadeadas = () => {
    if (!atividadeEncadeadas || atividadeEncadeadas.length === 0) return false;

    const atividadeEncadeadasIds = atividadeEncadeadas.flatMap(atividadeEncadeada =>
      atividadeEncadeada.atividades.map(item => item.id)
    );
    return atividadesSelecionadas.every(atividadeSelecionada =>
      atividadeEncadeadasIds.includes(atividadeSelecionada.id)
    );
  };

  return (
    <Fragment>
      {useIconWithLabel || useJustLabel ? (
        <>
          <ActionButton
            id={id ? `btn-excluir-atividade-${id}` : "btn-excluir-atividade"}
            type={useJustLabel ? "text" : "menu"}
            onClick={() => abrirModalExcluir()}
            icon={!useJustLabel && <Delete />}
            label={intl.formatMessage({ defaultMessage: "Excluir" })}
            style={useJustLabel && { color: "#fff" }}
          />
        </>
      ) : (
        <IconButton onClick={() => abrirModalExcluir()}>
          <Delete style={styleIcon} />
        </IconButton>
      )}
      {atividadesSelecionadas?.length > 1 && atividadeEncadeadas && atividadeEncadeadas.length > 0 ? (
        <ModalAtividadesEncadeadas
          open={open}
          close={close}
          cancelar={close}
          excluirNaoEncadeada={excluirNaoEncadeada}
          excluirComEncadeada={excluirComEncadeada}
          leadsAtividades={atividadeEncadeadas}
          isSomenteEncadeadas={verificarSomenteEncadeadas()}
        />
      ) : (
        <Dialog open={open} onClose={() => close(false)}>
          <ModalExcluirAtividade cancelar={() => close(false)} excluir={excluirNaoEncadeada} />
        </Dialog>
      )}
    </Fragment>
  );
}

ExcluirAtividade.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  atividadesSelecionadas: PropTypes.array,
  callback: PropTypes.func,
  styleIcon: PropTypes.object,
  useIconWithLabel: PropTypes.bool,
  useJustLabel: PropTypes.bool,
};

export default ExcluirAtividade;
